import React, { useEffect, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { useSearchParams } from 'react-router-dom'
import OAuthConsentApi from '../../../lib/api/oauthConsent'
import { isSafeContinueUrl, onUnauthorized, planNote, UNAUTHORIZED } from '../../../lib/oauthConsent'
import { setLoading } from '../../../action/loading-actions'
import { authRefreshRequest, logout } from '../../../action/auth-actions'
import './index.scss'

/**
 * OAuth sign-in and consent (WEB-7106), where services sends a user who is connecting BaseMap to Claude,
 * ChatGPT, a partner app, or signing in to developers.basemap.com.
 *
 * Reached as /oauth/consent?oauthRequest=<id>. Like /link, the route in ../index.js renders <Login /> here
 * when signed out, without navigating, so the request id survives sign-in and sign-up and this mounts with
 * the query string intact. 'oauth/consent' is in REDIRECT_EXCLUDED_PATHS in main.js because the ChatGPT and
 * Claude mobile apps open this page in a phone browser.
 *
 * Web never builds the redirect or sees the authorization code: services returns continueUrl, which goes
 * back to /connect/authorize, and that is where the code (or access_denied) is issued to the client.
 */

const STATUS = {
  LOADING: 'loading',
  READY: 'ready',          // awaiting Approve or Deny
  CONTINUING: 'continuing', // decision recorded, leaving for the client
  ERROR: 'error'           // unknown, expired, already used, a disabled app, or a failed call
}

const OAuthConsent = props => {
  const [searchParams] = useSearchParams()
  const oauthRequest = searchParams.get('oauthRequest')

  const [status, setStatus] = useState(STATUS.LOADING)
  const [details, setDetails] = useState(null)
  const [message, setMessage] = useState('')
  const [submitting, setSubmitting] = useState(false)
  // Once a decision is sent the request is spent. A token refresh re-running the effect below must not
  // send a second one, which services would refuse as already completed.
  const decided = useRef(false)
  // One refresh per visit: a second 401 means the session is gone, so sign in again (WEB-7128).
  const refreshTried = useRef(false)

  const { token, refreshToken, user, setLoading, logout, refreshSession } = props

  // A 401 is an expired session, not a missing request. A new token re-runs the consent call below; signing
  // out renders <Login /> here with ?oauthRequest= intact.
  const handleUnauthorized = () => {
    if (onUnauthorized({ refreshTried: refreshTried.current, hasRefreshToken: !!refreshToken }) === UNAUTHORIZED.REFRESH) {
      refreshTried.current = true
      refreshSession().catch(() => logout())
      return
    }
    logout()
  }

  useEffect(() => {
    setLoading(false)
  }, [setLoading])

  const decide = approve => {
    if (decided.current) return Promise.resolve()
    decided.current = true
    setSubmitting(true)
    setMessage('')

    return OAuthConsentApi.complete({ oauthRequest, approve, token })
      .then(({ continueUrl }) => {
        if (!isSafeContinueUrl(continueUrl, __API_URI__)) {
          throw new Error('Something went wrong finishing the sign-in. Go back to the app and connect again.')
        }
        setStatus(STATUS.CONTINUING)
        window.location.assign(continueUrl)
      })
      .catch(err => {
        if (err.status === 401) {
          // Not spent: services refused the token, not the decision. Ask again once signed in.
          decided.current = false
          setStatus(STATUS.LOADING)
          handleUnauthorized()
          return
        }
        setMessage(err.message)
        setStatus(STATUS.ERROR)
      })
      .finally(() => {
        setSubmitting(false)
      })
  }

  useEffect(() => {
    if (!oauthRequest) {
      setStatus(STATUS.ERROR)
      setMessage('This link is missing its sign-in request. Go back to the app and connect again.')
      return
    }

    if (decided.current) return

    let cancelled = false

    OAuthConsentApi.getConsent({ oauthRequest, token })
      .then(res => {
        if (cancelled) return
        setDetails(res)
        // BaseMap's own clients, such as developers.basemap.com: the user is signing in to BaseMap
        // itself, not granting a third party access, so there's no consent screen and no plan note.
        if (res.client && res.client.firstParty) {
          setStatus(STATUS.CONTINUING)
          decide(true)
        } else {
          setStatus(STATUS.READY)
        }
      })
      .catch(err => {
        if (cancelled) return
        if (err.status === 401) {
          handleUnauthorized()
          return
        }
        setMessage(err.message)
        setStatus(STATUS.ERROR)
      })

    // A late response for an earlier token must not overwrite the state a newer one produced.
    return () => { cancelled = true }
    // decide is recreated each render and only reads current values.
  }, [oauthRequest, token])

  const client = details && details.client
  const note = details ? planNote(details.plan) : null

  return (
    <div className='oauth-consent-bg'>
      <div className='oauth-consent-card'>
        {status === STATUS.LOADING && (
          <p className='oauth-consent-body'>Checking the sign-in request…</p>
        )}

        {status === STATUS.CONTINUING && (
          <p className='oauth-consent-body'>
            {client ? `Taking you back to ${client.name}…` : 'Taking you back…'}
          </p>
        )}

        {status === STATUS.READY && client && (
          <>
            {client.logoUri && (
              <img className='oauth-consent-logo' src={client.logoUri} alt='' referrerPolicy='no-referrer' />
            )}
            <h1 className='oauth-consent-header'>{`${client.name} wants to use your BaseMap account`}</h1>
            {client.partner && <p className='oauth-consent-partner'>{`From ${client.partner}`}</p>}

            <p className='oauth-consent-subhead'>It will be able to:</p>
            <ul className='oauth-consent-scopes'>
              {(details.scopes || []).map(scope => (
                <li key={scope.name}>{scope.description || scope.name}</li>
              ))}
            </ul>

            {note && (
              <div className='oauth-consent-plan'>
                <p>{note}</p>
                {details.plan.upgradeUrl && (
                  // A new tab, so this sign-in request isn't lost while the user upgrades.
                  <a href={details.plan.upgradeUrl} target='_blank' rel='noopener noreferrer'>
                    Upgrade
                  </a>
                )}
              </div>
            )}

            <p className='oauth-consent-note'>
              {`After you approve, you'll go back to ${details.redirectHost || client.name}.`}
              {details.redirectIsLoopback && (
                ' That\'s a program on your own computer. Only approve if you just started this from an app you trust.'
              )}
            </p>

            <div className='oauth-consent-actions'>
              <button className='oauth-consent-approve' disabled={submitting} onClick={() => decide(true)}>
                {submitting ? 'Working…' : 'Approve'}
              </button>
              <button className='oauth-consent-deny' disabled={submitting} onClick={() => decide(false)}>
                Deny
              </button>
            </div>

            <p className='oauth-consent-account'>
              {'You can disconnect it at any time in '}
              {/* A new tab, so this sign-in request isn't lost. */}
              <a href='/connected-apps' target='_blank' rel='noopener noreferrer'>Connected apps</a>.
            </p>
            {user && user.email && (
              <p className='oauth-consent-account'>
                {`Signed in as ${user.email}. `}
                <button className='oauth-consent-link' onClick={() => logout()}>Not you?</button>
              </p>
            )}
          </>
        )}

        {status === STATUS.ERROR && (
          <>
            <h1 className='oauth-consent-header'>We couldn&apos;t finish connecting</h1>
            <p className='oauth-consent-body oauth-consent-error'>{message}</p>
            <p className='oauth-consent-note'>
              Sign-in requests expire after a few minutes. Go back to the app you were connecting and start again.
            </p>
          </>
        )}
      </div>
    </div>
  )
}

const mapStateToProps = state => ({
  token: state.auth.token,
  refreshToken: state.auth.refreshToken,
  user: state.user
})

const mapDispatchToProps = dispatch => ({
  setLoading: bool => dispatch(setLoading(bool)),
  logout: () => dispatch(logout()),
  refreshSession: () => dispatch(authRefreshRequest())
})

export default connect(mapStateToProps, mapDispatchToProps)(OAuthConsent)
